import { CompensationHeader } from './CompensationHeader';
import { EnvelopeTabs } from './EnvelopeTabs';

import type { CompensationCycle, CompensationCycleOverview } from '@/types/compensation';

export interface ReportNavigationProps {
    cycles: CompensationCycle[];
    selectedCycleId: string | undefined;
    /** `undefined` while unknown: the tabs would otherwise offer a first envelope over a list that
     * only looks empty. */
    overview: CompensationCycleOverview | undefined;
    activeEnvelopeId: string | undefined;
    /** Company-wide scope: sets the cycle budget and sees every manager's envelopes. */
    managesAllDepartments: boolean;
    isCreating: boolean;
    onCycleChange: (cycleId: string) => void;
    onEnvelopeSelect: (envelopeId: string) => void;
    onCreate: () => void;
    onCycleBudgetChange: (amount: number | null) => void;
    /** Runs a navigation away from the open envelope, or holds it while a draft is unsaved. */
    guardNavigation?: (navigate: () => void) => void;
}

const navigateNow = (navigate: () => void) => navigate();

/** The cycle selector and the envelope tabs: everything that moves the reader to another envelope. */
export const ReportNavigation = ({
    cycles,
    selectedCycleId,
    overview,
    activeEnvelopeId,
    managesAllDepartments,
    isCreating,
    onCycleChange,
    onEnvelopeSelect,
    onCreate,
    onCycleBudgetChange,
    guardNavigation = navigateNow,
}: ReportNavigationProps) => (
    <>
        <CompensationHeader
            cycles={cycles}
            selectedCycleId={selectedCycleId}
            onCycleChange={(cycleId) => guardNavigation(() => onCycleChange(cycleId))}
        />

        {overview ? (
            <EnvelopeTabs
                envelopes={overview.envelopes}
                activeEnvelopeId={activeEnvelopeId}
                totalBudget={overview.totalBudget}
                cycleBudget={overview.cycleBudget}
                cycleOverrun={overview.cycleOverrun}
                canSetCycleBudget={managesAllDepartments}
                showsOwners={managesAllDepartments}
                onSelect={(envelopeId) => guardNavigation(() => onEnvelopeSelect(envelopeId))}
                onCreate={() => guardNavigation(onCreate)}
                onCycleBudgetChange={onCycleBudgetChange}
                isCreating={isCreating}
            />
        ) : null}
    </>
);
